import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
         AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle }
  from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, ReadOnlyNote } from "@/components/Common";
import { ItemForm } from "@/components/ItemForm";

/** Table + toolbar. Row creation/editing is delegated to <ItemForm />. */
export const DataManager = ({ testidPrefix, endpoint, fields, columns, items, reload,
                              emptyIcon, emptyTitle,
                              templates, templateLabel, onApplyTemplate }) => {
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delId, setDelId] = useState(null);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setOpen(true); };

  const remove = async () => {
    try {
      await api.delete(`/${endpoint}/${delId}`);
      toast.success("Deleted");
      setDelId(null);
      reload();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <Card className="p-0 overflow-hidden border-slate-200">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <span className="text-sm text-slate-500">{items.length} record{items.length !== 1 ? "s" : ""}</span>
        {isAdmin ? (
          <Button data-testid={`${testidPrefix}-add-button`} onClick={openNew}
            className="bg-pink-700 hover:bg-pink-800 gap-1.5 h-9">
            <Plus className="w-4 h-4" /> Add
          </Button>
        ) : <ReadOnlyNote />}
      </div>

      {items.length === 0 ? (
        <EmptyState icon={emptyIcon} title={emptyTitle}
          hint={isAdmin ? "Click Add to create your first record." : "No records yet."} />
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid={`${testidPrefix}-table`}>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {columns.map((c) => <TableHead key={c.name} className="text-slate-600">{c.label}</TableHead>)}
                {isAdmin && <TableHead className="text-right text-slate-600">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} data-testid={`${testidPrefix}-row-${row.id}`} className="hover:bg-slate-50/60">
                  {columns.map((c) => (
                    <TableCell key={c.name} className={c.mono ? "font-mono text-xs" : "text-sm"}>
                      {c.render ? c.render(row) : (row[c.name] || "—")}
                    </TableCell>
                  ))}
                  {isAdmin && (
                    <TableCell className="text-right whitespace-nowrap">
                      <Button data-testid={`${testidPrefix}-edit-${row.id}`} variant="ghost" size="icon"
                        className="h-8 w-8" onClick={() => openEdit(row)}>
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button data-testid={`${testidPrefix}-delete-${row.id}`} variant="ghost" size="icon"
                        className="h-8 w-8" onClick={() => setDelId(row.id)}>
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ItemForm testidPrefix={testidPrefix} endpoint={endpoint} fields={fields}
        open={open} onOpenChange={setOpen} editing={editing} onSaved={reload}
        templates={templates} templateLabel={templateLabel} onApplyTemplate={onApplyTemplate} />

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testidPrefix}-confirm-delete`} onClick={remove}
              className="bg-rose-600 hover:bg-rose-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
